<script setup lang="ts">
import { ButtonSize } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';

import IconMessage from '@/icons/line/message.svg';

defineProps<{
  isSubmitDisabled?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  send: [];
  focus: [];
}>();

const message = defineModel<string>();
const textarea = ref<HTMLTextAreaElement | null>(null);
const inputWrapper = ref<HTMLDivElement | null>(null);
const isFocused = ref(false);

function focusTextarea() {
  textarea.value?.focus();
}

function handleFocus() {
  isFocused.value = true;
  emit('focus');
}

function handleClickOutside() {
  isFocused.value = false;

  if (textarea.value) {
    textarea.value.scrollTop = 0;
  }
}

onMounted(() => {
  function handleClick(event: MouseEvent) {
    if (inputWrapper.value && !inputWrapper.value.contains(event.target as Node)) {
      handleClickOutside();
    }
  }

  document.addEventListener('click', handleClick);

  onUnmounted(() => {
    document.removeEventListener('click', handleClick);
  });
});

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    emit('send');
  }
}

defineExpose({
  focusTextarea
});
</script>

<template>
  <form @submit.prevent="emit('send')">
    <div
      ref="inputWrapper"
      @click.self="focusTextarea"
      :disabled="isDisabled"
      :class="[
        'relative flex flex-1 flex-col',
        'rounded-lg border border-slate-200 bg-white  duration-150 ease-in-out',
        'focus-within:border-slate-700 focus-within:ring-3 focus-within:ring-slate-700/40',
        'hover:border-slate-700',
        isFocused ? 'h-26' : 'h-10.5'
      ]"
    >
      <textarea
        v-bind="$attrs"
        ref="textarea"
        placeholder="Type your message..."
        :class="[
          'flex-1',
          'resize-none rounded-lg border-none px-3 py-1.5 text-[16px] text-slate-900 outline-0 md:text-sm',
          'focus:border-none focus:ring-0',
          'disabled:opacity-60 disabled:hover:border-slate-200',
          'transition-all duration-150 ease-in-out',
          {
            'line-clamp-1 overflow-hidden': !isFocused && message,
            'max-h-16': isFocused
          }
        ]"
        :disabled="isDisabled"
        v-model="message"
        maxlength="1000"
        @keydown="handleKeyDown"
        @focus="handleFocus"
        @click="handleFocus"
      />
      <IconMessage
        v-if="!isFocused && !message"
        class="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
      />
      <Button
        v-if="isFocused"
        :size="ButtonSize.sm"
        :iconRight="IconMessage"
        :isDisabled="!message || isLoading || isDisabled || isSubmitDisabled"
        :isLoading="isLoading"
        class="absolute bottom-1.5 right-2"
        type="submit"
        >Send</Button
      >
    </div>
  </form>
</template>
